<!-- TEMPORARY - scanner engine benchmark, remove after arbitration -->
<template>
    <div class="benchmark-camera">
        <video ref="video" autoplay muted playsinline class="benchmark-camera-video" />

        <div v-if="use_roi" class="benchmark-camera-roi" :style="roi_style" />

        <div class="benchmark-camera-hud">
            <div class="benchmark-camera-hud-line font-weight-bold">{{ engine.label }}</div>
            <div class="benchmark-camera-hud-line">{{ (elapsed_ms / 1000).toFixed(1) }} s / {{ timeout_seconds }} s</div>
            <div class="benchmark-camera-hud-line">{{ attempts }} tentatives &middot; {{ decode_errors }} erreurs</div>
            <div class="benchmark-camera-hud-line">dernier décodage {{ Math.round(last_decode_ms) }} ms</div>
            <div class="benchmark-camera-hud-line">{{ resolution_label }} &middot; ROI {{ roi_label }}</div>
        </div>

        <div v-if="error_message" class="benchmark-camera-error">
            {{ error_message }}
        </div>

        <div class="benchmark-camera-btn back-btn" @click="cancel">
            <v-icon color="white">mdi-arrow-left</v-icon>
        </div>

        <div v-if="has_torch" class="benchmark-camera-btn torch-btn" @click="toggleTorch">
            <v-icon color="white">{{ torch_on ? 'mdi-flashlight-off' : 'mdi-flashlight' }}</v-icon>
        </div>
    </div>
</template>

<script>
export const CAMERA_PROFILES = {
    hq_roi: {
        label: 'Haute rés. + ROI',
        description: 'Demande du 1920x1080 et recadre le centre à la densité de pixels native',
        constraints: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        use_roi: true,
    },
    legacy_640: {
        label: '640x480 (legacy)',
        description: 'Image entière, sans recadrage — reproduit les conditions de la tentative html5-qrcode',
        constraints: { facingMode: { ideal: 'environment' }, width: { ideal: 640 }, height: { ideal: 480 } },
        use_roi: false,
    },
}

// Wide enough for a Code 128 label, tall enough for a QR code.
const ROI_WIDTH_RATIO = 0.8
const ROI_HEIGHT_RATIO = 0.45

const SCAN_TIMEOUT_MS = 30000
const SCAN_INTERVAL_MS = 80
const ELAPSED_REFRESH_MS = 100

export default {
    name: 'ScannerBenchmarkCameraComponent',
    props: {
        engine: {
            type: Object,
            required: true,
        },
        camera_profile: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            error_message: '',
            elapsed_ms: 0,
            attempts: 0,
            decode_errors: 0,
            last_decode_ms: 0,
            torch_on: false,
            track_settings: null,
            capabilities: null,
            roi_width: 0,
            roi_height: 0,
        }
    },
    computed: {
        profile() {
            return CAMERA_PROFILES[this.camera_profile]
        },
        use_roi() {
            return this.profile.use_roi
        },
        has_torch() {
            return Boolean(this.capabilities && this.capabilities.torch)
        },
        timeout_seconds() {
            return SCAN_TIMEOUT_MS / 1000
        },
        resolution_label() {
            if (!this.track_settings) {
                return 'résolution inconnue'
            }

            return `${this.track_settings.width}x${this.track_settings.height}`
        },
        roi_label() {
            return this.roi_width ? `${this.roi_width}x${this.roi_height}` : '-'
        },
        roi_style() {
            return {
                width: `${ROI_WIDTH_RATIO * 100}%`,
                height: `${ROI_HEIGHT_RATIO * 100}%`,
            }
        },
    },
    created() {
        // Kept off `data` on purpose: Vue would make the canvas and the media stream reactive for no benefit.
        this.scan_canvas = document.createElement('canvas')
        // Without this hint Chrome keeps the canvas GPU-backed and every getImageData round-trips, which would be
        // charged to the decoder instead of the capture.
        this.scan_context = this.scan_canvas.getContext('2d', { willReadFrequently: true })
        this.stream = null
        this.video_track = null
        this.scanning = false
        this.decode_ms_samples = []
        this.first_frame_at = 0
        this.camera_open_ms = 0
        this.elapsed_timer_id = null
    },
    mounted() {
        this.start()
    },
    beforeDestroy() {
        this.stop()
    },
    methods: {
        async start() {
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                this.error_message = 'Caméra indisponible : cette page doit être servie en HTTPS (ou sur localhost).'
                return
            }

            const opened_at = performance.now()

            try {
                this.stream = await navigator.mediaDevices.getUserMedia({ video: this.profile.constraints, audio: false })
            } catch (error) {
                this.error_message = `Caméra refusée : ${error.name}. Vérifiez l’autorisation du navigateur pour ce site.`
                return
            }

            this.video_track = this.stream.getVideoTracks()[0]
            this.track_settings = this.video_track.getSettings()
            this.capabilities = this.video_track.getCapabilities ? this.video_track.getCapabilities() : null

            await this.applyFocusMode()

            const video = this.$refs.video
            video.srcObject = this.stream

            try {
                await video.play()
            } catch (error) {
                this.error_message = `Impossible de démarrer l’aperçu vidéo : ${error.name}`
                return
            }

            this.camera_open_ms = performance.now() - opened_at
            this.first_frame_at = performance.now()
            this.startElapsedTimer()
            this.scanning = true
            this.scanLoop()
        },

        async applyFocusMode() {
            const focus_modes = this.capabilities && this.capabilities.focusMode

            if (!focus_modes || !focus_modes.includes('continuous')) {
                return
            }

            try {
                // Applied on its own: grouping it with torch or zoom would make one unsupported constraint reject the others.
                await this.video_track.applyConstraints({ advanced: [{ focusMode: 'continuous' }] })
            } catch (error) {
                // The camera keeps its default focus behaviour, which is a valid result to observe.
            }
        },

        async toggleTorch() {
            try {
                await this.video_track.applyConstraints({ advanced: [{ torch: !this.torch_on }] })
                this.torch_on = !this.torch_on
            } catch (error) {
                this.$store.commit('snackbar/showSnackbar', { color: 'error', text: 'Torche refusée par cet appareil' })
            }
        },

        startElapsedTimer() {
            this.elapsed_timer_id = setInterval(() => {
                this.elapsed_ms = performance.now() - this.first_frame_at
            }, ELAPSED_REFRESH_MS)
        },

        async scanLoop() {
            while (this.scanning) {
                let result = null
                const image_data = this.grabRegionOfInterest()

                // The first frames after play() can still report a 0x0 video size; capturing then would throw.
                if (!image_data) {
                    await new Promise((resolve) => setTimeout(resolve, SCAN_INTERVAL_MS))
                    continue
                }

                const started_at = performance.now()

                try {
                    result = await this.engine.detect(image_data)
                } catch (error) {
                    // A decode that throws is not a frame without a barcode; conflating the two would make the run unreadable.
                    this.decode_errors++
                }

                this.last_decode_ms = performance.now() - started_at
                this.decode_ms_samples.push(this.last_decode_ms)
                this.attempts++

                if (!this.scanning) {
                    return
                }

                if (result) {
                    this.finish('success', result)
                    return
                }

                if (performance.now() - this.first_frame_at >= SCAN_TIMEOUT_MS) {
                    this.finish('no_read', null)
                    return
                }

                await new Promise((resolve) => setTimeout(resolve, SCAN_INTERVAL_MS))
            }
        },

        grabRegionOfInterest() {
            const video = this.$refs.video
            const width = video.videoWidth
            const height = video.videoHeight

            if (!width || !height) {
                return null
            }

            const region_width = this.use_roi ? Math.round(width * ROI_WIDTH_RATIO) : width
            const region_height = this.use_roi ? Math.round(height * ROI_HEIGHT_RATIO) : height
            const source_x = Math.round((width - region_width) / 2)
            const source_y = Math.round((height - region_height) / 2)

            if (this.scan_canvas.width !== region_width || this.scan_canvas.height !== region_height) {
                this.scan_canvas.width = region_width
                this.scan_canvas.height = region_height
                this.roi_width = region_width
                this.roi_height = region_height
            }

            // Source and destination sizes are identical: no downscale, so dense Code 128 bars keep their native pixel density.
            this.scan_context.drawImage(video, source_x, source_y, region_width, region_height, 0, 0, region_width, region_height)

            return this.scan_context.getImageData(0, 0, region_width, region_height)
        },

        finish(outcome, result) {
            const elapsed_ms = performance.now() - this.first_frame_at
            this.scanning = false

            this.$emit('finished', {
                outcome: outcome,
                engine_id: this.engine.id,
                engine_label: this.engine.label,
                camera_profile: this.camera_profile,
                decoded_text: result ? result.text : '',
                decoded_format: result ? String(result.format) : '',
                ttfr_ms: outcome === 'success' ? Math.round(elapsed_ms) : null,
                elapsed_ms: Math.round(elapsed_ms),
                attempts: this.attempts,
                decode_errors: this.decode_errors,
                decode_ms_median: Math.round(this.medianDecodeMs()),
                camera_open_ms: Math.round(this.camera_open_ms),
                resolution: this.resolution_label,
                roi: this.roi_label,
                capabilities: this.capabilities,
            })
        },

        medianDecodeMs() {
            if (!this.decode_ms_samples.length) {
                return 0
            }

            const sorted = this.decode_ms_samples.slice().sort((a, b) => a - b)
            const middle = Math.floor(sorted.length / 2)

            return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2
        },

        stop() {
            this.scanning = false

            if (this.elapsed_timer_id) {
                clearInterval(this.elapsed_timer_id)
                this.elapsed_timer_id = null
            }

            if (this.stream) {
                this.stream.getTracks().forEach((track) => track.stop())
                this.stream = null
                this.video_track = null
            }
        },

        cancel() {
            this.$emit('cancel')
        },
    },
}
</script>

<style scoped>
.benchmark-camera {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 100;
    background-color: #000000;
}

.benchmark-camera-video {
    width: 100%;
    height: 100%;
    /* `contain` rather than `cover`: the operator must see exactly the frame the decoder receives. */
    object-fit: contain;
}

.benchmark-camera-roi {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border: 2px solid #1ba0db;
    pointer-events: none;
}

.benchmark-camera-hud {
    position: absolute;
    bottom: 16px;
    left: 16px;
    right: 16px;
    padding: 10px 12px;
    border-radius: 6px;
    background-color: rgba(0, 0, 0, 0.6);
    color: #ffffff;
    font-size: 12px;
    line-height: 1.5;
}

.benchmark-camera-hud-line {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.benchmark-camera-error {
    position: absolute;
    top: 50%;
    left: 16px;
    right: 16px;
    transform: translateY(-50%);
    padding: 16px;
    border-radius: 6px;
    background-color: rgba(200, 0, 0, 0.85);
    color: #ffffff;
    font-size: 14px;
    text-align: center;
}

.benchmark-camera-btn {
    position: absolute;
    width: 46px;
    height: 46px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 100%;
    background-color: rgba(100, 100, 100, 0.5);
    cursor: pointer;
}

.benchmark-camera-btn.back-btn {
    top: 12px;
    left: 16px;
}

.benchmark-camera-btn.torch-btn {
    top: 12px;
    right: 16px;
}
</style>
